import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase, authHelpers, getProdSupabaseClient } from '@/lib/supabase/client';
import { devAuthService } from '@/lib/supabase/devAuth';
import { User as SupabaseUser, Session } from '@supabase/supabase-js';
import {
  User,
  Organization,
  Role,
  Permission,
  Team,
  UserStatus,
  SystemRole,
  PermissionScope,
  AuditLog,
  UserApproval,
  UserSource
} from '@/types';

interface UserProfile extends User {
  organization?: Organization;
  roles: Role[];
  permissions: Permission[];
  teams: Team[];
  directPermissions: Permission[];
}

interface PermissionCheck {
  resource: string;
  action: string;
  scope?: PermissionScope;
  context?: Record<string, any>;
}

interface AuthContextType {
  // Core authentication
  user: SupabaseUser | null;
  userProfile: UserProfile | null;
  session: Session | null;
  loading: boolean; // Auth session check only (fast)
  profileLoading: boolean; // Profile data loading (background)
  error: string | null;

  // Approval system
  approvalStatus: UserApproval | null;
  approvalLoading: boolean;

  // Organization and roles
  organization: Organization | null;
  roles: Role[];
  permissions: Permission[];
  teams: Team[];

  // Permission checking
  hasPermission: (check: PermissionCheck) => boolean;
  hasRole: (roleName: string) => boolean;
  hasSystemRole: (systemRole: SystemRole) => boolean;
  canAccess: (resource: string, action: string, scope?: PermissionScope) => boolean;

  // User management
  signUp: (
    email: string,
    password: string,
    fullName?: string
  ) => Promise<{ data: any; error: any }>; // Stub - throws error
  signIn: (email: string, password: string) => Promise<{ data: any; error: any }>;
  signInWithMagicLink: (email: string) => Promise<{ data: any; error: any }>;
  signInWithOAuth: (
    provider: 'google' | 'github' | 'discord'
  ) => Promise<{ data: any; error: any }>; // Stub - throws error
  signOut: () => Promise<void>;
  requestApproval: (
    userId: string,
    fullName: string,
    authProvider: string
  ) => Promise<{ data: any; error: any }>; // Stub - throws error
  checkApprovalStatus: () => Promise<void>;

  // Profile management
  updateProfile: (updates: Partial<User>) => Promise<{ data: any; error: any }>;
  refreshUserProfile: () => Promise<void>;

  // Audit logging
  logAction: (
    action: string,
    resourceType: string,
    resourceId?: string,
    details?: Record<string, any>
  ) => Promise<void>;

  // Activity tracking
  updateLastActive: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const EnhancedAuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true); // Auth session check only
  const [profileLoading, setProfileLoading] = useState(false); // Profile data loading (background)
  const [error, setError] = useState<string | null>(null);
  const [approvalStatus, setApprovalStatus] = useState<UserApproval | null>(null);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const isInitializingRef = useRef(false);
  const lastProcessedUserIdRef = useRef<string | null>(null);
  const profileLoadInProgressRef = useRef<Set<string>>(new Set());
  const approvalCheckInProgressRef = useRef<Set<string>>(new Set());
  const lastActiveUpdateRef = useRef<number>(0);

  // Cache management helpers
  const PROFILE_CACHE_KEY = 'vehix_user_profile_cache';
  const PROFILE_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  const getCachedProfile = useCallback((userId: string): UserProfile | null => {
    try {
      const cached = localStorage.getItem(PROFILE_CACHE_KEY);
      if (!cached) return null;

      const { profile, timestamp, cachedUserId } = JSON.parse(cached);

      // Check if cache is for same user and still valid
      if (cachedUserId !== userId) return null;
      if (Date.now() - timestamp > PROFILE_CACHE_TTL) return null;

      return profile;
    } catch (err) {
      return null;
    }
  }, []);

  const setCachedProfile = useCallback((userId: string, profile: UserProfile) => {
    try {
      localStorage.setItem(
        PROFILE_CACHE_KEY,
        JSON.stringify({
          profile,
          timestamp: Date.now(),
          cachedUserId: userId
        })
      );
    } catch (err) {}
  }, []);

  // Load user profile with all related data
  const loadUserProfile = useCallback(
    async (userId: string, userData?: SupabaseUser | null, useCache = true) => {
      // If profile was already successfully loaded, skip
      if (lastProcessedUserIdRef.current === userId) {
        return;
      }

      // Try to load from cache first for instant UX
      if (useCache) {
        const cachedProfile = getCachedProfile(userId);
        if (cachedProfile) {
          setUserProfile(cachedProfile);
          setRoles(cachedProfile.roles);
          setPermissions(cachedProfile.permissions);
          setTeams(cachedProfile.teams);
          lastProcessedUserIdRef.current = userId;
          setProfileLoading(true); // Indicate background refresh
        }
      }

      // If profile load is already in progress, skip
      if (profileLoadInProgressRef.current.has(userId)) {
        return;
      }

      // Mark this user as being processed to prevent duplicate loads
      profileLoadInProgressRef.current.add(userId);

      // Declare profileTimeout at function scope so it's accessible in catch block
      let profileTimeout: ReturnType<typeof setTimeout> | null = null;

      try {
        // Set a global timeout for the entire profile loading process
        profileTimeout = setTimeout(() => {
          const fallbackProfile: UserProfile = {
            id: userId,
            email: userData?.email || '',
            firstName: userData?.user_metadata?.['full_name']?.split(' ')[0] || 'User',
            lastName: userData?.user_metadata?.['full_name']?.split(' ')[1] || '',
            status: UserStatus.ACTIVE,
            emailVerified: userData?.email_confirmed_at ? true : false,
            lastLoginAt: new Date().toISOString(),
            onboardingCompleted: false,
            source: (userData?.app_metadata?.provider as UserSource) || UserSource.EMAIL,
            organizationId: 'dev-org',
            isActive: true,
            roles: [],
            permissions: [],
            teams: [],
            directPermissions: [],
            createdAt: userData?.created_at || new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };
          setUserProfile(fallbackProfile);
        }, 15000); // 15 second timeout - give more time for profile creation

        // For OAuth users, try to load full profile first, then fallback to basic if needed
        if (userData?.app_metadata?.provider && userData.app_metadata.provider !== 'email') {
          try {
            // Use prod schema client - user_roles is in prod schema
            const prodClient = getProdSupabaseClient();

            const rolesPromise = prodClient
              .from('user_roles')
              .select(
                `
              role_id,
              roles (
                id,
                name,
                display_name,
                description,
                level,
                scope
              )
            `
              )
              .eq('user_id', userId)
              .eq('is_active', true);

            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(() => reject(new Error('User roles query timeout')), 8000)
            );

            const { data: userRoles, error: rolesError } = (await Promise.race([
              rolesPromise,
              timeoutPromise
            ])) as any;

            if (!rolesError && userRoles && userRoles.length > 0) {
              // Continue with full profile loading below
            } else {
              const basicProfile: UserProfile = {
                id: userId,
                email: userData?.email || '',
                firstName: userData?.user_metadata?.['full_name']?.split(' ')[0] || 'User',
                lastName: userData?.user_metadata?.['full_name']?.split(' ')[1] || '',
                status: UserStatus.ACTIVE,
                emailVerified: userData?.email_confirmed_at ? true : false,
                lastLoginAt: new Date().toISOString(),
                onboardingCompleted: false,
                source: UserSource.OAUTH,
                organizationId: 'dev-org',
                isActive: true,
                roles: [],
                permissions: [],
                teams: [],
                directPermissions: [],
                createdAt: userData?.created_at || new Date().toISOString(),
                updatedAt: new Date().toISOString()
              };

              setUserProfile(basicProfile);

              profileLoadInProgressRef.current.delete(userId);
              if (profileTimeout) clearTimeout(profileTimeout);
              return;
            }
          } catch (err) {
            const basicProfile: UserProfile = {
              id: userId,
              email: userData?.email || '',
              firstName: userData?.user_metadata?.['full_name']?.split(' ')[0] || 'User',
              lastName: userData?.user_metadata?.['full_name']?.split(' ')[1] || '',
              status: UserStatus.ACTIVE,
              emailVerified: userData?.email_confirmed_at ? true : false,
              lastLoginAt: new Date().toISOString(),
              onboardingCompleted: false,
              source: UserSource.OAUTH,
              organizationId: 'dev-org',
              isActive: true,
              roles: [],
              permissions: [],
              teams: [],
              directPermissions: [],
              createdAt: userData?.created_at || new Date().toISOString(),
              updatedAt: new Date().toISOString()
            };

            setUserProfile(basicProfile);

            profileLoadInProgressRef.current.delete(userId);
            if (profileTimeout) clearTimeout(profileTimeout);
            return;
          }
        }

        // Load user roles from database
        // Use prod schema - all new users have roles in prod.user_roles

        const prodClient = getProdSupabaseClient();

        // RLS policy "Users can select own roles" uses (SELECT auth.uid()) = user_id
        // tenant_id filter is not needed — RLS works with just user_id
        const rolesQuery = prodClient
          .from('user_roles')
          .select(
            `
          role_id,
          tenant_id,
          roles (
            id,
            name,
            display_name,
            description,
            level,
            scope
          )
        `
          )
          .eq('user_id', userId)
          .eq('is_active', true);

        // Add timeout to prevent hanging
        const rolesPromise = rolesQuery;

        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('User roles query timeout')), 8000)
        );

        const { data: userRoles, error: rolesError } = (await Promise.race([
          rolesPromise,
          timeoutPromise
        ])) as any;

        // Declare at function scope so it's accessible later for profile creation
        let userRolesList: Role[] = [];

        if (rolesError) {
          // Fallback to public schema for backward compatibility

          const fallbackPromise = supabase
            .from('user_roles')
            .select(
              `
            role_id,
            roles (
              id,
              name,
              display_name,
              description,
              level,
              scope
            )
          `
            )
            .eq('user_id', userId)
            .eq('is_active', true);

          const { data: fallbackRoles, error: fallbackError } = (await Promise.race([
            fallbackPromise,
            timeoutPromise
          ])) as any;

          if (fallbackError) {
            throw fallbackError;
          }

          if (fallbackRoles && fallbackRoles.length > 0) {
            // Use fallback roles
            const roleMapping: Record<string, SystemRole> = {
              super_admin: SystemRole.SUPER_ADMIN,
              organization_owner: SystemRole.ORGANIZATION_OWNER,
              dealership_admin: SystemRole.DEALERSHIP_ADMIN,
              dealer_admin: SystemRole.DEALERSHIP_ADMIN,
              fixed_operations_director: SystemRole.FIXED_OPERATIONS_DIRECTOR,
              field_operations_director: SystemRole.FIXED_OPERATIONS_DIRECTOR,
              operations_director: SystemRole.FIXED_OPERATIONS_DIRECTOR,
              service_manager: SystemRole.SERVICE_MANAGER,
              service_advisor: SystemRole.SERVICE_ADVISOR,
              lead_technician: SystemRole.LEAD_TECHNICIAN,
              technician: SystemRole.TECHNICIAN,
              viewer: SystemRole.VIEWER
            };

            userRolesList = (fallbackRoles || []).map((ur: any) => ({
              id: ur.roles.id,
              name: ur.roles.name,
              displayName: ur.roles.display_name,
              description: ur.roles.description || '',
              systemRole: roleMapping[ur.roles.name] || SystemRole.VIEWER,
              organizationId: 'dev-org',
              tenantId: ur.tenant_id ?? null,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            }));

            setRoles(userRolesList);

            // Continue with permissions loading below
          } else {
            throw rolesError; // Throw original error
          }
        } else {
          // Map role name to SystemRole enum
          const roleMapping: Record<string, SystemRole> = {
            super_admin: SystemRole.SUPER_ADMIN,
            organization_owner: SystemRole.ORGANIZATION_OWNER,
            dealership_admin: SystemRole.DEALERSHIP_ADMIN,
            dealer_admin: SystemRole.DEALERSHIP_ADMIN,
            fixed_operations_director: SystemRole.FIXED_OPERATIONS_DIRECTOR,
            field_operations_director: SystemRole.FIXED_OPERATIONS_DIRECTOR,
            operations_director: SystemRole.FIXED_OPERATIONS_DIRECTOR,
            service_manager: SystemRole.SERVICE_MANAGER,
            service_advisor: SystemRole.SERVICE_ADVISOR,
            lead_technician: SystemRole.LEAD_TECHNICIAN,
            technician: SystemRole.TECHNICIAN,
            viewer: SystemRole.VIEWER
          };

          // Transform roles data
          userRolesList = (userRoles || []).map((ur: any) => ({
            id: ur.roles.id,
            name: ur.roles.name,
            displayName: ur.roles.display_name,
            description: ur.roles.description || '',
            systemRole: roleMapping[ur.roles.name] || SystemRole.VIEWER,
            organizationId: 'dev-org', // TODO: Get from actual organization
            tenantId: ur.tenant_id ?? null,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }));

          setRoles(userRolesList);
        }

        // Load permissions from active_user_permissions view
        // Use prod schema - permissions are in prod schema

        // Build permissions query - use tenant_id if available (may be required for RLS)
        const permsQuery = prodClient
          .from('active_user_permissions')
          .select('permission_id, resource, action, name, scope, granted')
          .eq('user_id', userId)
          .eq('granted', true);

        // Add timeout to prevent hanging
        const permsPromise = permsQuery;

        const permsTimeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Permissions query timeout')), 8000)
        );

        const { data: userPermissions, error: permsError } = (await Promise.race([
          permsPromise,
          permsTimeoutPromise
        ])) as any;

        // Declare at function scope so it's accessible later for profile creation
        let userPermissionsList: Permission[] = [];

        if (permsError) {
          // Fallback to public schema for backward compatibility

          const fallbackPermsPromise = supabase
            .from('active_user_permissions')
            .select('permission_id, resource, action, name, scope, granted')
            .eq('user_id', userId)
            .eq('granted', true);

          const { data: fallbackPerms, error: fallbackPermsError } = (await Promise.race([
            fallbackPermsPromise,
            permsTimeoutPromise
          ])) as any;

          if (fallbackPermsError) {
            throw fallbackPermsError;
          }

          if (fallbackPerms && fallbackPerms.length > 0) {
            // Use fallback permissions - continue with mapping below
            const scopeMapping: Record<string, PermissionScope> = {
              self: PermissionScope.SELF,
              team: PermissionScope.TEAM,
              department: PermissionScope.DEPARTMENT,
              dealer: PermissionScope.DEALERSHIP,
              dealership: PermissionScope.DEALERSHIP,
              organization: PermissionScope.ORGANIZATION,
              all: PermissionScope.ALL
            };

            userPermissionsList = (fallbackPerms || []).map((up: any) => ({
              id: up.permission_id,
              resource: up.resource,
              action: up.action,
              name: up.name,
              scope: scopeMapping[up.scope] || PermissionScope.ALL,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            }));

            setPermissions(userPermissionsList);
            setTeams([]);
          } else {
            throw permsError; // Throw original error
          }
        } else {
          // Map scope string to PermissionScope enum
          const scopeMapping: Record<string, PermissionScope> = {
            self: PermissionScope.SELF,
            team: PermissionScope.TEAM,
            department: PermissionScope.DEPARTMENT,
            dealer: PermissionScope.DEALERSHIP, // Map 'dealer' to DEALERSHIP
            dealership: PermissionScope.DEALERSHIP,
            organization: PermissionScope.ORGANIZATION,
            all: PermissionScope.ALL
          };

          // Transform permissions data
          userPermissionsList = (userPermissions || []).map((up: any) => ({
            id: up.permission_id,
            resource: up.resource, // This comes from the view which maps resource_type as resource
            action: up.action,
            name: up.name,
            scope: scopeMapping[up.scope] || PermissionScope.ALL,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }));

          setPermissions(userPermissionsList);
          setTeams([]);
        }

        // Create user profile using auth data and loaded roles/permissions
        // CRITICAL: Use the freshly loaded data directly (userRolesList/userPermissionsList)
        // NOT state values (roles/permissions) which may not have updated yet due to async state updates

        const profile: UserProfile = {
          id: userId,
          email: userData?.email || '',
          firstName: userData?.user_metadata?.['full_name']?.split(' ')[0] || 'User',
          lastName: userData?.user_metadata?.['full_name']?.split(' ')[1] || '',
          status: UserStatus.ACTIVE,
          emailVerified: userData?.email_confirmed_at ? true : false,
          lastLoginAt: new Date().toISOString(),
          onboardingCompleted: true,
          source: UserSource.MANUAL,
          organizationId: 'dev-org', // TODO: Get from actual organization
          isActive: true,
          roles: userRolesList, // Use freshly loaded data, not state
          permissions: userPermissionsList, // Use freshly loaded data, not state
          teams: [],
          directPermissions: userPermissionsList, // Use freshly loaded data, not state
          createdAt: userData?.created_at || new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        setUserProfile(profile);

        // Cache the profile for fast future loads
        setCachedProfile(userId, profile);

        // Mark as successfully processed and clear in-progress flag
        lastProcessedUserIdRef.current = userId;
        profileLoadInProgressRef.current.delete(userId);
        setProfileLoading(false); // Background refresh complete
        if (profileTimeout) clearTimeout(profileTimeout);
      } catch (err) {
        // Create a minimal fallback profile if database queries fail

        const fallbackProfile: UserProfile = {
          id: userId,
          email: userData?.email || '',
          firstName: userData?.user_metadata?.['full_name']?.split(' ')[0] || 'User',
          lastName: userData?.user_metadata?.['full_name']?.split(' ')[1] || '',
          status: UserStatus.ACTIVE,
          emailVerified: userData?.email_confirmed_at ? true : false,
          lastLoginAt: new Date().toISOString(),
          onboardingCompleted: false,
          source: UserSource.OAUTH,
          organizationId: 'dev-org',
          isActive: true,
          roles: [],
          permissions: [],
          teams: [],
          directPermissions: [],
          createdAt: userData?.created_at || new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        setUserProfile(fallbackProfile);

        // Clear in-progress flag even for fallback (don't set lastProcessedUserIdRef to allow retry)
        profileLoadInProgressRef.current.delete(userId);
        setProfileLoading(false); // Stop loading indicator even on error
        if (profileTimeout) clearTimeout(profileTimeout);
      }
    },
    [getCachedProfile, setCachedProfile]
  );

  // Permission checking functions
  const hasPermission = useCallback(
    (check: PermissionCheck): boolean => {
      if (!userProfile) {
        return false;
      }

      const { resource, action, scope = PermissionScope.SELF, context } = check;

      // Check direct permissions first
      const directPermission = userProfile.directPermissions.find(
        p => p.resource === resource && p.action === action
      );

      if (directPermission) {
        return checkPermissionScope(directPermission, scope, context);
      }

      // Check role permissions
      const rolePermission = userProfile.permissions.find(
        p => p.resource === resource && p.action === action
      );

      if (rolePermission) {
        return checkPermissionScope(rolePermission, scope, context);
      }

      return false;
    },
    [userProfile]
  );

  const checkPermissionScope = (
    _permission: Permission,
    scope: PermissionScope,
    _context?: Record<string, any>
  ): boolean => {
    // For now, implement basic scope checking
    // This can be enhanced with more complex logic based on context
    switch (scope) {
      case PermissionScope.SELF:
        return true;
      case PermissionScope.TEAM:
        return (userProfile?.teams?.length || 0) > 0;
      case PermissionScope.DEPARTMENT:
        return !!userProfile?.department;
      case PermissionScope.DEALERSHIP:
        return !!userProfile?.organizationId;
      case PermissionScope.ORGANIZATION:
        return !!userProfile?.organizationId;
      case PermissionScope.ALL:
        return hasSystemRole(SystemRole.SUPER_ADMIN);
      default:
        return true;
    }
  };

  const hasRole = useCallback(
    (roleName: string): boolean => {
      return userProfile?.roles.some(role => role.name === roleName) || false;
    },
    [userProfile]
  );

  const hasSystemRole = useCallback(
    (systemRole: SystemRole): boolean => {
      return userProfile?.roles.some(role => role.systemRole === systemRole) || false;
    },
    [userProfile]
  );

  const canAccess = useCallback(
    (resource: string, action: string, scope?: PermissionScope): boolean => {
      return hasPermission({ resource, action, scope: scope || PermissionScope.SELF });
    },
    [hasPermission]
  );

  // Check approval status
  const checkApprovalStatus = useCallback(
    async (userData?: SupabaseUser | null) => {
      if (!userData) return;

      // Prevent duplicate approval checks for the same user
      if (approvalCheckInProgressRef.current.has(userData.id)) {
        return;
      }

      // If we already have an approved status for this user, don't check again
      if (
        approvalStatus &&
        approvalStatus.userId === userData.id &&
        approvalStatus.status === 'approved'
      ) {
        setApprovalLoading(false);
        return;
      }

      try {
        approvalCheckInProgressRef.current.add(userData.id);
        setApprovalLoading(true);

        // For OAuth users, try to check existing approval with retries and proper fallback
        if (userData.app_metadata?.provider && userData.app_metadata.provider !== 'email') {
          let approvalFound = false;

          // Get prod client for user_approvals query
          const prodClient = getProdSupabaseClient();

          // Try multiple times with increasing timeouts
          for (let attempt = 1; attempt <= 3; attempt++) {
            try {
              const existingApprovalPromise = prodClient
                .from('user_approvals')
                .select(
                  'id, user_id, email, full_name, auth_provider, status, requested_at, approved_at, approved_by, notes, created_at, updated_at, tenant_id'
                )
                .eq('user_id', userData.id)
                .maybeSingle();

              const timeoutMs = attempt * 1000; // 1s, 2s, 3s - faster feedback
              const timeoutPromise = new Promise((_, reject) =>
                setTimeout(
                  () => reject(new Error(`Approval query timeout (attempt ${attempt})`)),
                  timeoutMs
                )
              );

              const { data: existingApproval, error: approvalError } = (await Promise.race([
                existingApprovalPromise,
                timeoutPromise
              ])) as any;

              if (existingApproval) {
                const approvalData: UserApproval = {
                  id: existingApproval.id,
                  userId: existingApproval.user_id,
                  email: existingApproval.email,
                  fullName: existingApproval.full_name,
                  authProvider: existingApproval.auth_provider,
                  status: existingApproval.status as 'pending' | 'approved' | 'rejected',
                  requestedAt: existingApproval.requested_at,
                  approvedAt: existingApproval.approved_at || undefined,
                  approvedBy: existingApproval.approved_by || undefined,
                  notes: existingApproval.notes || undefined,
                  createdAt: existingApproval.created_at || new Date().toISOString(),
                  updatedAt: existingApproval.updated_at || new Date().toISOString()
                };

                // Always set the approval status from database, overriding any error status
                setApprovalStatus(approvalData);

                approvalFound = true;
                setApprovalLoading(false);

                // For approved users, profile creation should happen via loadUserProfile
                if (approvalData.status === 'approved') {
                }

                approvalCheckInProgressRef.current.delete(userData.id);
                return;
              } else if (!approvalError) {
                // Don't create pending status immediately - let subsequent checks retry
                // This prevents blocking OAuth users who should be approved
                approvalFound = false;
                break;
              } else {
                if (attempt < 3) {
                  await new Promise(resolve => setTimeout(resolve, 500));
                }
              }
            } catch (err) {
              if (attempt < 3) {
                await new Promise(resolve => setTimeout(resolve, 500));
              }
            }
          }

          if (!approvalFound) {
            // Don't create error status immediately - let subsequent auth state changes retry
            // This prevents error status from blocking successful approval checks
          }

          setApprovalLoading(false);
          approvalCheckInProgressRef.current.delete(userData.id);
          return;
        }

        // Only check approval status for email users (not OAuth users)
        if (userData.app_metadata?.provider && userData.app_metadata.provider !== 'email') {
          setApprovalLoading(false);
          approvalCheckInProgressRef.current.delete(userData.id);
          return;
        }

        // Check actual approval status from database for email users
        // Use prod schema - all new users are created in prod.user_approvals

        const prodClient = getProdSupabaseClient();

        // Query prod.user_approvals (RLS policy allows user_id = auth.uid())
        const query = prodClient
          .from('user_approvals')
          .select(
            'id, user_id, email, full_name, auth_provider, status, requested_at, approved_at, approved_by, notes, created_at, updated_at, tenant_id'
          )
          .eq('user_id', userData.id);

        // Add timeout to prevent hanging
        const approvalPromise = query.maybeSingle();

        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Approval query timeout')), 5000)
        );

        const { data, error } = (await Promise.race([approvalPromise, timeoutPromise])) as any;

        if (error) {
          // If there's a policy error (42P17), it might be RLS issue
          if (error.code === '42P17') {
            // For now, set as pending so user sees the pending screen instead of error
            setApprovalStatus({
              id: 'temp-pending',
              userId: userData.id,
              email: userData.email!,
              fullName:
                userData.user_metadata?.['full_name'] || userData.email?.split('@')[0] || 'User',
              authProvider: userData.app_metadata?.provider || 'email',
              status: 'pending',
              requestedAt: new Date().toISOString(),
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            });
            return;
          }

          if (error.code === 'PGRST116') {
            // No approval record found in prod.user_approvals
            // This should not happen for admin-created users
            // User must be created by admin via Control Center → User Setup

            setApprovalStatus(null);
            return;
          }
        } else if (!data) {
          // No approval record found in prod.user_approvals
          // This should not happen for admin-created users
          // User must be created by admin via Control Center → User Setup

          setApprovalStatus(null);
          return;
        } else {
          // Approval record found

          // All admin-created users should be approved
          setApprovalStatus({
            id: data.id,
            userId: data.user_id,
            email: data.email,
            fullName: data.full_name || undefined,
            authProvider: data.auth_provider || 'email', // Always 'email' for admin-created users
            status: data.status as 'pending' | 'approved' | 'rejected',
            requestedAt: data.requested_at,
            approvedAt: data.approved_at || undefined,
            approvedBy: data.approved_by || undefined,
            notes: data.notes || undefined,
            createdAt: data.created_at || new Date().toISOString(),
            updatedAt: data.updated_at || new Date().toISOString()
          });
        }
      } catch (err) {
        setApprovalStatus(null);
      } finally {
        setApprovalLoading(false);
        approvalCheckInProgressRef.current.delete(userData.id);
      }
    },
    [approvalStatus]
  );

  // requestApproval removed - users are created by admin and auto-approved
  // No self-signup, so no need for approval requests
  const requestApproval = useCallback(
    async (userId: string, fullName: string, authProvider: string) => {
      throw new Error(
        'Approval requests are disabled. Users are created by admin and auto-approved.'
      );
    },
    []
  );

  // Update user profile
  const updateProfile = useCallback(
    async (updates: Partial<User>) => {
      if (!user) throw new Error('No user logged in');

      try {
        const { data, error } = await supabase
          .from('users')
          .update(updates)
          .eq('id', user.id)
          .select()
          .single();

        if (error) throw error;

        // Refresh user profile
        await loadUserProfile(user.id);

        return { data, error: null };
      } catch (err) {
        return { data: null, error: err };
      }
    },
    [user, loadUserProfile]
  );

  // Refresh user profile
  const refreshUserProfile = useCallback(async () => {
    if (user) {
      await loadUserProfile(user.id);
    }
  }, [user, loadUserProfile]);

  // Audit logging
  const logAction = useCallback(
    async (
      action: string,
      resourceType: string,
      resourceId?: string,
      details?: Record<string, any>
    ) => {
      if (!user) return;

      try {
        const auditLog: Partial<AuditLog> = {
          userId: user.id,
          organizationId: userProfile?.organizationId || '',
          action,
          resourceType,
          resourceId: resourceId || '',
          success: true,
          ...(details && { metadata: details }),
          createdAt: new Date().toISOString()
        };

        await supabase.from('audit_logs').insert(auditLog);
      } catch (err) {}
    },
    [user, userProfile]
  );

  // signUp removed - self-signup is disabled
  // Users are created by admin via Control Center → User Setup
  // Admin creates users with username, email, role, and tenant
  // Users are auto-approved when created by admin
  const signUp = useCallback(async (email: string, password: string, fullName?: string) => {
    throw new Error(
      'Self-signup is disabled. Please contact your administrator to create an account.'
    );
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);

      // Check if this is a development sign-in
      if (devAuthService.isDevelopmentMode()) {
        const devCreds = devAuthService.getDevCredentials();
        if (email === devCreds.email && password === devCreds.password) {
          const { data, error } = await devAuthService.createDevUser(
            email,
            password,
            devCreds.fullName
          );
          if (error) {
            // Check if it's an email verification error and provide helpful instructions
            if (
              error &&
              typeof error === 'object' &&
              'message' in error &&
              typeof error.message === 'string' &&
              error.message.includes('Email verification required')
            ) {
              const instructions = devAuthService.getEmailVerificationInstructions(email);

              // Set a more helpful error message
              (error as Error).message =
                `${(error as Error).message}\n\nInstructions:\n${instructions.instructions.join('\n')}\n\n${instructions.alternative}`;
            }
            throw error;
          }
          return { data, error: null };
        }
      }

      const { data, error } = await authHelpers.signIn(email, password);

      if (error) throw error;

      return { data, error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Sign in failed';
      setError(errorMessage);
      return { data: null, error: err };
    } finally {
      setLoading(false);
    }
  }, []);

  const signInWithMagicLink = useCallback(async (email: string) => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await authHelpers.signInWithMagicLink(email);

      if (error) throw error;

      return { data, error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Magic link sign in failed';
      setError(errorMessage);
      return { data: null, error: err };
    } finally {
      setLoading(false);
    }
  }, []);

  // signInWithOAuth removed - Google OAuth is disabled
  // Only email/password authentication is allowed
  const signInWithOAuth = useCallback(async (provider: 'google' | 'github' | 'discord') => {
    throw new Error('OAuth sign-in is disabled. Please use email/password to sign in.');
  }, []);

  const signOut = useCallback(async () => {
    try {
      setLoading(true);
      await authHelpers.signOut();

      // Clear all state
      setUser(null);
      setUserProfile(null);
      setSession(null);
      setApprovalStatus(null);
      setOrganization(null);
      setRoles([]);
      setPermissions([]);
      setTeams([]);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign out failed');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      if (isInitializingRef.current) return; // Prevent multiple simultaneous initializations

      try {
        isInitializingRef.current = true;
        setLoading(true);

        // Get initial session
        const {
          data: { session },
          error
        } = await supabase.auth.getSession();

        if (error) {
          return;
        }

        setSession(session);
        setUser(session?.user ?? null);

        // Load profile and check approval in background (don't block)
        if (session?.user) {
          loadUserProfile(session.user.id, session.user).catch(err => {});
          checkApprovalStatus(session.user).catch(err => {});
        } else if (devAuthService.shouldAutoLogin()) {
          // Auto-login in development mode if no session
          const devCreds = devAuthService.getDevCredentials();

          try {
            const { data, error: loginError } = await authHelpers.signIn(
              devCreds.email,
              devCreds.password
            );
            if (loginError) {
            } else if (data?.user) {
              // Auth state change listener will handle profile loading
            }
          } catch (autoLoginError) {}
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Authentication initialization failed');
      } finally {
        // Set loading false immediately - don't wait for profile
        setLoading(false);
        isInitializingRef.current = false;
      }
    };

    initializeAuth();

    // Listen for auth changes
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      const currentUserId = session?.user?.id;

      // Prevent processing the same user multiple times
      if (currentUserId && lastProcessedUserIdRef.current === currentUserId) {
        return;
      }

      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        // Load profile and check approval in background (non-blocking)

        loadUserProfile(session.user.id, session.user).catch(err => {});
        checkApprovalStatus(session.user).catch(err => {});
      } else {
        // Clear state on sign out
        lastProcessedUserIdRef.current = null;
        setUserProfile(null);
        setApprovalStatus(null);
        setOrganization(null);
        setRoles([]);
        setPermissions([]);
        setTeams([]);
      }
    });

    return () => subscription.unsubscribe();
  }, []); // Empty dependency array - this should only run once on mount

  // Update last active timestamp
  const updateLastActive = useCallback(async () => {
    if (!user) return;

    const now = Date.now();
    // Debounce updates to once per minute
    if (now - lastActiveUpdateRef.current < 60000) {
      return;
    }

    lastActiveUpdateRef.current = now;

    try {
      // const timestamp = new Date().toISOString();
      // Update user_approvals table
      // const { error } = await supabase
      //   .from('user_approvals')
      //   .update({ last_active_at: timestamp })
      //   .eq('user_id', user.id);
      // if (error) {
      //   // If column doesn't exist or other error, log but don't fail
      //   console.warn('Failed to update last_active_at:', error);
      // }
    } catch (err) {}
  }, [user]);

  // Track user activity
  useEffect(() => {
    if (!user) return;

    const handleActivity = () => {
      updateLastActive();
    };

    // Listen for common user interactions
    window.addEventListener('mousedown', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('touchstart', handleActivity);
    window.addEventListener('scroll', handleActivity);

    // Initial update
    updateLastActive();

    return () => {
      window.removeEventListener('mousedown', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
      window.removeEventListener('scroll', handleActivity);
    };
  }, [user, updateLastActive]);

  const value: AuthContextType = {
    user,
    userProfile,
    session,
    loading,
    profileLoading,
    error,
    approvalStatus,
    approvalLoading,
    organization,
    roles,
    permissions,
    teams,
    hasPermission,
    hasRole,
    hasSystemRole,
    canAccess,
    signUp, // Stub - throws error (self-signup disabled)
    signIn,
    signInWithMagicLink,
    signInWithOAuth, // Stub - throws error (OAuth disabled)
    signOut,
    requestApproval, // Stub - throws error (approval requests disabled)
    checkApprovalStatus,
    updateProfile,
    refreshUserProfile,
    logAction,
    updateLastActive
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
